// @flow
import { actions as serverActions } from 'state/reducers/serverReducer';
import { actions as authActions } from 'state/reducers/authReducer';
import { logout } from 'state/commands/authCommands';
import type {
  BackupConfiguration,
  DirectoryServiceDescription,
  Dispatch,
  EmailServerConfiguration,
  GetState,
  LoggingConfig,
  BrandingSettings,
  MobileUploadSettings,
} from 'state/types';
import type { Ports } from 'state/types/serverTypes';
import {
  addNotification,
  ERROR_LEVEL,
  SUCCESS_LEVEL,
} from 'portfolio-shared/state/commands/globalNotificationsCommands';
import { actions as modalPromptActions } from 'portfolio-shared/state/reducers/modalPromptReducer';
import logger from 'portfolio-shared/util/logging';
import {
  getAdminSession,
  getBackupRestoreStatus,
  getIdentitySession,
  getIsAdminServerRestarting,
  getServerRunState,
  getUsername,
  getIdentityEncryptedPassword,
} from 'state/selectors';
import {
  SERVER_STATE_STARTED,
  SERVER_STATE_STARTING,
  SERVER_STATE_STOPPING,
} from 'util/constants';
import localizedStrings from 'util/localizedStrings';
import { ADMIN_PROPERTIES } from 'util/AdminProperties';
import {
  hideBackupRestoreProgress,
  isBackupRestoreBusy,
  isBackupRestoreCancelled,
  showBackupRestoreProgress,
} from 'util/backupUtils';
import { getAdminAddressAfterPortChange } from 'util/serverUtils';

const {
  backup: backupAction,
  cancelBackup: cancelBackupAction,
  clearEmailServerConfigValidating,
  createDirectoryServiceDescription: createDirectoryServiceDescriptionAction,
  deleteDirectoryServiceDescription: deleteDirectoryServiceDescriptionAction,
  getAdminProperties: getAdminPropertiesAction,
  restartServer: restartServerAction,
  restore: restoreAction,
  sendDiagnosticData: sendDiagnosticDataAction,
  setBackupConfig: setBackupConfigAction,
  setBrandingCustomImage: setBrandingCustomImageAction,
  setBrandingSettings: setBrandingSettingsAction,
  setEmailServerConfig: setEmailServerConfigAction,
  setEmailServerConfigValidating,
  setGlobalSettings: setGlobalSettingsAction,
  setPorts: setPortsAction,
  setMobileUploadSettings: setMobileUploadSettingsAction,
  setIsCommunicatingWithDirectoryService,
  setIsRestarting,
  setIsAdminServerRestarting,
  setLoggingConfig: setLoggingConfigAction,
  setServerStatus,
  startServer: startServerAction,
  stopServer: stopServerAction,
  updateDirectoryServiceDescription: updateDirectoryServiceDescriptionAction,
  validateEmailServerConfig: validateEmailServerConfigAction,
} = serverActions;
const { clearIdentitySession, identityLogin } = authActions;
const { showModalPrompt } = modalPromptActions;

export function getAdminProperties(properties: Array<string>) {
  return async function getAdminProperties(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const {
      auth: { adminSessionId, identitySessionId },
    } = getState();

    const isAdminServerRestarting = getIsAdminServerRestarting(getState());
    if (isAdminServerRestarting) {
      logger.info('admin server restarting, skipping getting admin properties');
    } else if (properties.length > 0) {
      const oldRunState = getServerRunState(getState());
      await dispatch(
        getAdminPropertiesAction(adminSessionId, identitySessionId, properties)
      ).catch(e => {
        dispatch(logout());
        logger.info('failed to get admin properties');
      });

      if (properties.includes(ADMIN_PROPERTIES.serverStatus)) {
        const newRunState = getServerRunState(getState());
        if (
          newRunState !== SERVER_STATE_STARTED &&
          oldRunState === SERVER_STATE_STARTED
        ) {
          // The server switched from started to not-started.
          dispatch(clearIdentitySession());
        } else if (
          newRunState === SERVER_STATE_STARTED &&
          oldRunState !== SERVER_STATE_STARTED
        ) {
          await dispatch(
            identityLogin(
              getUsername(getState()),
              getIdentityEncryptedPassword(getState())
            )
          );
        }
      }

      const backupRestoreStatus = getBackupRestoreStatus(getState());
      logger.debug({ backupRestoreStatus });
      if (isBackupRestoreBusy(backupRestoreStatus)) {
        showBackupRestoreProgress(dispatch, getState);
      } else if (isBackupRestoreCancelled(backupRestoreStatus)) {
        hideBackupRestoreProgress(dispatch, getState);
      }
    } else {
      logger.info('failed to get admin properties, no properties provided');
    }
  };
}

export function startServer() {
  return async function startServer(dispatch: Dispatch, getState: GetState) {
    const adminSessionId = getAdminSession(getState());
    if (adminSessionId) {
      await dispatch(setServerStatus(SERVER_STATE_STARTING));
      await dispatch(startServerAction(adminSessionId));
    }
  };
}

export function stopServer() {
  return async function stopServer(dispatch: Dispatch, getState: GetState) {
    const adminSessionId = getAdminSession(getState());
    if (adminSessionId) {
      await dispatch(setServerStatus(SERVER_STATE_STOPPING));
      await dispatch(clearIdentitySession());
      await dispatch(stopServerAction(adminSessionId));
    }
  };
}

export function restartServer() {
  return async function restartServer(dispatch: Dispatch, getState: GetState) {
    const adminSessionId = getAdminSession(getState());
    if (adminSessionId) {
      await dispatch(setServerStatus(SERVER_STATE_STOPPING));
      await dispatch(setIsRestarting(true));
      await dispatch(clearIdentitySession());
      await dispatch(restartServerAction(adminSessionId));
    }
  };
}

export function sendDiagnosticData(
  accountName: string,
  contactName: ?string,
  techSupportTicketNum: ?string
) {
  return async function sendDiagnosticData(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getAdminSession(getState());

    if (sessionId) {
      await dispatch(
        sendDiagnosticDataAction(
          sessionId,
          accountName,
          contactName,
          techSupportTicketNum
        )
      );
      await dispatch(
        getAdminProperties([ADMIN_PROPERTIES.diagnosticDataStatus])
      );
    }
  };
}

export function createDirectoryServiceDescription(
  description: DirectoryServiceDescription
) {
  return async function createDirectoryServiceDescription(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getIdentitySession(getState());
    if (sessionId) {
      let error;
      dispatch(setIsCommunicatingWithDirectoryService(true));
      await dispatch(
        createDirectoryServiceDescriptionAction(sessionId, description)
      ).catch(e => {
        error = e;
      });
      dispatch(setIsCommunicatingWithDirectoryService(false));
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.DIRECTORY_SERVICE_CONNECT_ERROR,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.IDENTITY_SERVICE_SUCCESSFUL_CREATION,
            SUCCESS_LEVEL
          )
        );
        await dispatch(
          getAdminProperties([ADMIN_PROPERTIES.directoryServiceDescription])
        );
      }
    }
  };
}

export function deleteDirectoryServiceDescription(uuid: string) {
  return async function deleteDirectoryServiceDescription(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getIdentitySession(getState());

    async function doDelete() {
      if (sessionId) {
        await dispatch(
          deleteDirectoryServiceDescriptionAction(sessionId, uuid)
        );
        await dispatch(
          getAdminProperties([ADMIN_PROPERTIES.directoryServiceDescription])
        );
      }
    }

    dispatch(
      showModalPrompt({
        dataTestName: 'confirm-delete-directory-service',
        message: localizedStrings.CONFIRM_DELETE_SERVICE,
        onButton2: doDelete,
      })
    );
  };
}

export function setGlobalSettings(settings: any) {
  return async function setGlobalSettings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      let error;
      await dispatch(setGlobalSettingsAction(sessionId, settings)).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
            SUCCESS_LEVEL
          )
        );
      }
      dispatch(getAdminProperties([ADMIN_PROPERTIES.globalSettings]));
    }
  };
}

export function setBrandingSettings(config: BrandingSettings, imageData: any) {
  return async function setBrandingSettings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const adminSession = getAdminSession(getState());
    if (adminSession) {
      let errorImageUpload;
      let errorSetConfig;

      // handle image upload first
      const formData: any = {};
      if (imageData.file) {
        formData.file = imageData.file;
        await dispatch(
          setBrandingCustomImageAction(formData, adminSession)
        ).catch(e => {
          errorImageUpload = e;
        });
      }

      if (errorImageUpload) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        // handle config next
        if (imageData.file) {
          config.useCustomImage = true;
        }

        await dispatch(setBrandingSettingsAction(adminSession, config)).catch(
          e => {
            errorSetConfig = e;
          }
        );
        if (errorSetConfig) {
          dispatch(
            addNotification(
              localizedStrings.NOTIFICATION_FAILURE_UPDATE,
              ERROR_LEVEL
            )
          );
        } else {
          dispatch(
            addNotification(
              localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
              SUCCESS_LEVEL
            )
          );
        }
        await dispatch(getAdminProperties([ADMIN_PROPERTIES.brandingSettings]));
      }
    }
  };
}

export function setMobileUploadSettings(settings: MobileUploadSettings) {
  return async function setMobileUploadSettings(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const adminSession = getAdminSession(getState());
    const identSession = getIdentitySession(getState());
    if (adminSession && identSession) {
      let error;
      await dispatch(
        setMobileUploadSettingsAction(adminSession, identSession, settings)
      ).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
            SUCCESS_LEVEL
          )
        );
      }
      dispatch(getAdminProperties([ADMIN_PROPERTIES.globalSettings]));
    }
  };
}

export function setPorts(ports: Ports) {
  return async function setPorts(dispatch: Dispatch, getState: GetState) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      const updatedAdminPort =
        ports['esp.port.admin-web-app'] || ports['esp.port.admin-web-app-ssl'];
      let error;
      await dispatch(setPortsAction(sessionId, ports)).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
            SUCCESS_LEVEL
          )
        );
      }

      if (updatedAdminPort) {
        dispatch(setIsAdminServerRestarting(true));
        dispatch(
          showModalPrompt({
            button1text: null,
            button2text: null,
            message: localizedStrings.PORT_WARNING_ADMIN_RESTART,
            messageArguments: { numSecs: 30 },
          })
        );

        // redirect to new location
        const curr = new URL(window.location.href);
        const address = getAdminAddressAfterPortChange(curr, ports);
        logger.info('Will redirect to ' + JSON.stringify(address));
        setTimeout(() => {
          window.location.replace(address);
        }, 30000);
      } else {
        await dispatch(getAdminProperties([ADMIN_PROPERTIES.ports]));
      }
    }
  };
}

export function setEmailServerConfig(config: EmailServerConfiguration) {
  return async function setEmailServerConfig(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      let error;
      await dispatch(setEmailServerConfigAction(sessionId, config)).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
            SUCCESS_LEVEL
          )
        );
      }
      await dispatch(
        getAdminProperties([ADMIN_PROPERTIES.emailServerConfiguration])
      );
    }
  };
}

const FAULT_EMAIL_NOT_FOUND = 'No SMTP server found';
const FAULT_EMAIL_AUTH_FAILED = 'Authentication failed';
export function validateEmailServerConfig(config: EmailServerConfiguration) {
  return async function setEmailServerConfig(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      dispatch(setEmailServerConfigValidating());
      let error;
      await dispatch(validateEmailServerConfigAction(sessionId, config)).catch(
        e => {
          error = e;
        }
      );
      dispatch(clearEmailServerConfigValidating());
      if (error) {
        var msg;
        if (error.message && error.message.includes(FAULT_EMAIL_NOT_FOUND)) {
          msg = localizedStrings.EMAIL_CONFIG_INVALID_SERVER_NOT_FOUND;
        } else if (
          error.message &&
          error.message.includes(FAULT_EMAIL_AUTH_FAILED)
        ) {
          msg = localizedStrings.EMAIL_CONFIG_INVALID_AUTHENTICATION_FAILED;
        } else {
          msg = localizedStrings.EMAIL_CONFIG_INVALID_GENERIC;
        }
        dispatch(addNotification(msg, ERROR_LEVEL));
      } else {
        dispatch(
          addNotification(
            localizedStrings.EMAIL_CONFIG_SUCCESS_MESSAGE,
            SUCCESS_LEVEL
          )
        );
      }
    }
  };
}

export function setLoggingConfig(config: LoggingConfig) {
  return async function setLoggingConfig(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      let error;
      await dispatch(setLoggingConfigAction(sessionId, config)).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
            SUCCESS_LEVEL
          )
        );
      }
      dispatch(getAdminProperties([ADMIN_PROPERTIES.loggingConfig]));
    }
  };
}

export function setBackupConfig(config: BackupConfiguration) {
  return async function setBackupConfig(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      let error;
      await dispatch(setBackupConfigAction(sessionId, config)).catch(e => {
        error = e;
      });
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_FAILURE_UPDATE,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.NOTIFICATION_SUCCESS_UPDATE,
            SUCCESS_LEVEL
          )
        );
      }
      await dispatch(getAdminProperties([ADMIN_PROPERTIES.backupConfig]));
    }
  };
}

function delayedGetBackupRestoreStatus(dispatch: Dispatch) {
  // Give the backup a little time to start before showing progress dialog
  setTimeout(
    () => dispatch(getAdminProperties([ADMIN_PROPERTIES.backupRestoreStatus])),
    1000
  );
}

export function backup() {
  return async function backup(dispatch: Dispatch, getState: GetState) {
    async function doBackup() {
      const sessionId = getAdminSession(getState());
      if (sessionId) {
        await dispatch(backupAction(sessionId));
        delayedGetBackupRestoreStatus(dispatch);
      }
    }

    dispatch(
      showModalPrompt({
        isButton2Default: true,
        message: localizedStrings.BACKUP_CONFIRMATION_VERBIAGE,
        onButton2: doBackup,
        dataTestName: 'backup-confirm',
      })
    );
  };
}

export function cancelBackup() {
  return async function cancelBackup(dispatch: Dispatch, getState: GetState) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      dispatch(cancelBackupAction(sessionId));
    }
  };
}

export function restore(restoreArchivePath: string) {
  return async function restore(dispatch: Dispatch, getState: GetState) {
    const sessionId = getAdminSession(getState());
    if (sessionId) {
      await dispatch(restoreAction(sessionId, restoreArchivePath));
      delayedGetBackupRestoreStatus(dispatch);
    }
  };
}

export function updateDirectoryServiceDescription(
  description: DirectoryServiceDescription
) {
  return async function updateDirectoryServiceDescription(
    dispatch: Dispatch,
    getState: GetState
  ) {
    const sessionId = getIdentitySession(getState());
    if (sessionId) {
      let error;
      dispatch(setIsCommunicatingWithDirectoryService(true));
      await dispatch(
        updateDirectoryServiceDescriptionAction(sessionId, description)
      ).catch(e => {
        error = e;
      });
      dispatch(setIsCommunicatingWithDirectoryService(false));
      if (error) {
        dispatch(
          addNotification(
            localizedStrings.DIRECTORY_SERVICE_CONNECT_ERROR,
            ERROR_LEVEL
          )
        );
      } else {
        dispatch(
          addNotification(
            localizedStrings.IDENTITY_SERVICE_SUCCESSFUL_CREATION,
            SUCCESS_LEVEL
          )
        );
        await dispatch(
          getAdminProperties([ADMIN_PROPERTIES.directoryServiceDescription])
        );
      }
    }
  };
}



// WEBPACK FOOTER //
// ./src/state/commands/adminPropertiesCommands.js